import React from 'react';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import {
  ScheduleOutlined as ClockIcon,
  BoltOutlined as EventIcon,
  PersonOutlineOutlined as PersonIcon,
} from '@mui/icons-material';
import { useInView, useLoop, usePrefersReducedMotion } from './motion';
import { status as statusTokens } from '../../theme';

// The hero picture: a morning running itself.
//
// It replaced a compliance meter, which was the wrong picture twice over —
// it sold the filing cabinet, and it was a still image of a number. A
// visitor who has never heard of this product cannot learn anything from a
// bar at 94%.
//
// This shows the thing the product actually does, as it happens: a routine
// starts on a clock, a step goes to a named person, nobody answers, and the
// engine escalates it. Four rows, no explanatory copy, and by the fourth
// one you know what the software is for.
//
// Built from the product's own status colours, so it cannot drift.

interface Row {
  at: string;
  label: string;
  detail: string;
  tone: string;
  trigger: 'clock' | 'event' | 'person';
}

const ROWS: Row[] = [
  { at: '06:00', label: 'Daily open', detail: 'Finished', tone: statusTokens.ok, trigger: 'clock' },
  { at: '07:15', label: 'Start-of-shift check', detail: 'Waiting on Dana W.', tone: statusTokens.warn, trigger: 'clock' },
  { at: '07:38', label: 'Van 214 out of service', detail: 'Repair flow started', tone: statusTokens.info, trigger: 'event' },
  { at: '07:52', label: 'Start-of-shift check', detail: 'No answer — escalated to manager', tone: statusTokens.danger, trigger: 'clock' },
];

const TRIGGER_ICON = {
  clock: <ClockIcon sx={{ fontSize: 15 }} />,
  event: <EventIcon sx={{ fontSize: 15 }} />,
  person: <PersonIcon sx={{ fontSize: 15 }} />,
};

const TRIGGER_LABEL = {
  clock: 'Started by the clock',
  event: 'Started by an event',
  person: 'Started by a person',
};

const OperationDemo: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, seen } = useInView<HTMLDivElement>('0px');
  // A fifth beat holds the finished state before it loops, so the story has
  // an ending rather than snapping back the instant it lands.
  const step = useLoop(ROWS.length + 1, 1700, seen);
  const shown = Math.min(step + 1, ROWS.length);
  const current = ROWS[Math.min(step, ROWS.length - 1)];

  return (
    <Card
      ref={ref}
      sx={{ p: { xs: 2.5, md: 3 }, boxShadow: '0 12px 40px rgba(22,21,15,0.08)', overflow: 'hidden' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="text.disabled">Today · Area 3</Typography>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{
            px: 1.25, py: 0.4, borderRadius: 999,
            bgcolor: 'primary.light', color: 'primary.dark',
            transition: reduced ? 'none' : 'opacity 300ms',
          }}
        >
          {TRIGGER_ICON[current.trigger]}
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {TRIGGER_LABEL[current.trigger]}
          </Typography>
        </Stack>
      </Stack>

      <Stack gap={0}>
        {ROWS.map((row, index) => {
          const visible = index < shown;
          const isCurrent = index === step;
          return (
            <Box
              key={row.label + row.at}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.75,
                py: 1.4,
                borderTop: index === 0 ? 0 : '1px solid',
                borderColor: 'divider',
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'translate3d(0, 10px, 0)',
                transition: reduced
                  ? 'none'
                  : 'opacity 480ms cubic-bezier(0.22,1,0.36,1), transform 480ms cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              <Box sx={{ position: 'relative', mt: 0.55, flexShrink: 0 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: row.tone }} />
                {/* The live row breathes. One element, one ring — a whole
                    list of pulsing dots would be a fire alarm. */}
                {isCurrent && !reduced && (
                  <Box
                    sx={{
                      position: 'absolute', inset: -4, borderRadius: '50%',
                      border: '2px solid', borderColor: row.tone,
                      animation: 'topoPulse 1700ms ease-out infinite',
                      '@keyframes topoPulse': {
                        '0%': { transform: 'scale(0.6)', opacity: 0.9 },
                        '70%': { transform: 'scale(1.35)', opacity: 0 },
                        '100%': { opacity: 0 },
                      },
                    }}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{row.label}</Typography>
                <Typography variant="caption" sx={{ color: row.tone, fontWeight: 500 }}>
                  {row.detail}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', mt: 0.35, flexShrink: 0 }}
              >
                {row.at}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Nobody typed any of this. The engine did.
      </Typography>
    </Card>
  );
};

export default OperationDemo;
